/**
 * ThirdPersonController: walk + orbiting follow camera + character facing.
 *
 * Extends CharacterController to reuse its capsule collision, gravity, jump,
 * ground probe, fallLimit and camera-relative (yaw) movement. After the base
 * physics step writes the eye position and look angles, this controller:
 *   - computes a behind/above camera offset (yaw+pitch orbit, over-the-shoulder),
 *   - raycasts the collision mesh to pull the camera out of walls,
 *   - writes the offset into state.cameraOffset (applied by PlayerRig),
 *   - orients the avatar to face the movement direction,
 *   - feeds speed/grounded/jumping into the animation state machine.
 *
 * Mouse look (pointer lock) orbits the camera; movement is relative to the
 * camera yaw. Wheel adjusts the follow distance.
 */

import { Vec3 } from 'playcanvas';

import type { CameraFrame, CameraState } from './types';
import { CharacterController } from './CharacterController';
import { applyFrameRotation, dampAngles, setBasisOffset, setCameraBasis } from './camera-utils';
import { damp, mod } from './math-utils';
import type { AvatarController } from './AvatarController';
import type { AnimationStateMachine } from './AnimationStateMachine';
import { SphereMover } from './sphere-mover';

/** Inputs passed to the animation state machine each frame. */
export interface AnimInputs {
    speed: number;
    grounded: boolean;
    jumping: boolean;
    running: boolean;
}

const forward = new Vec3();
const right = new Vec3();
const up = new Vec3();
const desired = new Vec3();
const dir = new Vec3();
const hitDelta = new Vec3();
const offset = new Vec3();

const FLY_CAMERA_RADIUS = 0.2;
const AVATAR_CAMERA_HIDE_DISTANCE = 1.0;
const AVATAR_CAMERA_SHOW_DISTANCE = 1.25;

/** 飞行贴地钳制：向下探针的起点余量与判定皮肤（米）。 */
const FLOOR_PROBE_MARGIN = 0.5;
const FLOOR_PROBE_SKIN = 0.1;

class ThirdPersonController extends CharacterController {
    /** Avatar (GLB or placeholder); set by main.ts after load. */
    avatar: AvatarController | null = null;
    /** Animation state machine; set by main.ts. */
    anim: AnimationStateMachine | null = null;
    /** Movement semantics while this third-person view is active. */
    moveMode: 'fly' | 'walk' = 'walk';

    // --- Follow camera params (proposal §四) ---
    distance = 3.2;
    height = 1.8;
    shoulderOffset = 0.45;
    minDistance = 2.0;
    maxDistance = 8.0;
    /** Camera collision pull-in margin (meters in front of the wall). */
    cameraMargin = 0.08;
    /** Movement speed for third-person fly mode. */
    flyMoveSpeed = 4;
    /** Maximum height (meters) the player can ascend above the fly entry point. */
    flyMaxHeight = 4;
    /**
     * 进场飞行待碰撞期：高度钳在进场落点（同 FirstPersonController.
     * holdEntryAltitude，walk 默认场景碰撞未到时由 main.ts 开启）。
     */
    holdEntryAltitude = false;

    /**
     * 飞行贴地钳制：眼睛位置到脚底的垂直距离（avatar eyeHeight + groundOffset）。
     * 飞行下压时脚底最多贴到地表、不陷入地面；0 = 禁用（编辑器自由飞）。
     * 由 main.ts 在形象加载/切换时同步。
     */
    flyFeetDrop = 1.5;

    private _flyMover = new SphereMover(FLY_CAMERA_RADIUS);
    private _flySpawnY = 0;
    private _avatarHiddenByCamera = false;
    // Character yaw for flight mode — follows the horizontal movement direction
    // (like walk mode). When idle the target is held so mouse orbit doesn't spin
    // the avatar; pressing S faces the camera.
    private _flightAvatarYaw = 0;
    private _flightAvatarYawTarget = 0;

    constructor() {
        super();
        // Orbit pitch clamp (degrees).
        this._minPitch = -35;
        this._maxPitch = 65;
        // Shift input scales both ground and airborne movement, so run-jumps
        // carry forward like a hurdle jump instead of becoming in-place hops.
        this.moveGroundSpeed = 2.2;
        this.moveAirSpeed = this.moveGroundSpeed;
        this.airAccel = this.groundAccel;
        this.jumpSpeed = 4.0;
        this.gravity = 9.8;
    }

    onEnter(state: CameraState): void {
        this._avatarHiddenByCamera = false;
        if (this.moveMode === 'fly') {
            this.goto(state);
            this._clampEntryPitch(state);
            this._flyMover.collision = this.collision;
            this._flyMover.reset(this._position);
            this._flySpawnY = this._position.y;
            this._spawn.store(this._position, this._angles);
            this._spawnGrounded = false;
            this._updateCamera(state);
            this._updateAvatarCameraVisibility(state);
            this._flightAvatarYaw = this._cameraForwardAvatarYaw(state);
            this._flightAvatarYawTarget = this._flightAvatarYaw;
            this._updateFlightAvatar(0, state);
            return;
        }
        super.onEnter(state);
        this._clampEntryPitch(state);
        this._updateCamera(state);
        this._updateAvatarCameraVisibility(state);
        this.avatar?.clearFlightPose();
        this.avatar?.snapFacing(this._cameraForwardAvatarYaw(state));
    }

    onExit(state: CameraState): void {
        // First-person controllers expect a zero camera offset.
        state.cameraOffset.set(0, 0, 0);
        this._avatarHiddenByCamera = false;
        this.avatar?.clearFlightPose();
        super.onExit(state);
    }

    update(deltaTime: number, inputFrame: CameraFrame, state: CameraState): void {
        if (this.moveMode === 'fly') {
            this._updateFly(deltaTime, inputFrame, state);
            return;
        }
        super.update(deltaTime, inputFrame, state);
        this._updateCamera(state);
        this._updateAvatarCameraVisibility(state);
        this._updateAvatar(deltaTime);
    }

    private _updateFly(deltaTime: number, inputFrame: CameraFrame, state: CameraState): void {
        const { move, rotate, running } = inputFrame.read();
        this._lastInputRunning = running;

        applyFrameRotation(this._targetAngles, rotate, this._minPitch, this._maxPitch);
        dampAngles(this._angles, this._targetAngles, this.rotateDamping, deltaTime);

        setCameraBasis(this._angles, forward, right, up);
        const s = this.flyMoveSpeed * deltaTime;
        setBasisOffset(offset, move[0] * s, move[1] * s, move[2] * s, forward, right, up);
        this._flyMover.collision = this.collision;
        this._flyMover.move(this._position, offset);

        // 最低高度钳制：飞行不能把脚压进地面。向下短探针找正下方地表，
        // 眼睛高度不低于 地表 + flyFeetDrop（脚底贴地）。
        if (this.flyFeetDrop > 0 && this.collision) {
            const hit = this.collision.queryRay(
                this._position.x, this._position.y + FLOOR_PROBE_MARGIN, this._position.z,
                0, -1, 0, this.flyFeetDrop + FLOOR_PROBE_MARGIN + FLOOR_PROBE_SKIN);
            if (hit) {
                const minY = hit.y + this.flyFeetDrop;
                if (this._position.y < minY) this._position.y = minY;
            }
        }

        if (this.holdEntryAltitude) {
            // 进场飞行期：高度保持与场景设定的初始位置一致（垂直位移全抵消）。
            this._position.y = this._flySpawnY;
        } else {
            const maxY = this._flySpawnY + this.flyMaxHeight;
            if (this._position.y > maxY) this._position.y = maxY;
        }

        state.position.copy(this._position);
        state.angles.set(this._angles.x, this._angles.y, 0);
        state.fov = this.fov;
        this._updateCamera(state);
        this._updateAvatarCameraVisibility(state);

        // Character facing follows the horizontal movement direction (like walk
        // mode): pressing S faces the camera; when idle the target is held so
        // mouse orbit doesn't spin the avatar.
        const hInput2 = move[0] * move[0] + move[2] * move[2];
        if (hInput2 > 1e-8) {
            const ox = forward.x * move[2] + right.x * move[0];
            const oz = forward.z * move[2] + right.z * move[0];
            this._flightAvatarYawTarget = Math.atan2(ox, oz) * 180 / Math.PI;
        }
        if (deltaTime > 0) {
            const t = damp(0.8, deltaTime);
            const diff = mod(this._flightAvatarYawTarget - this._flightAvatarYaw + 180, 360) - 180;
            this._flightAvatarYaw = mod(this._flightAvatarYaw + diff * t, 360);
        }

        this._updateFlightAvatar(deltaTime, state, move[2], this._flightAvatarYaw, move[0]);
    }

    private _updateCamera(state: CameraState): void {
        // Target = eye position (the rig root).
        const target = state.position;

        // Camera basis from look angles (includes pitch).
        setCameraBasis(state.angles, forward, right, up);
        // Desired camera: behind the player along -forward, shifted to the side.
        desired.copy(target);
        desired.addScaled(forward, -this.distance);
        desired.addScaled(right, this.shoulderOffset);

        // Direction & length from target to desired camera position.
        dir.copy(desired).sub(target);
        const len = dir.length();
        if (len < 1e-5) {
            state.cameraOffset.set(0, 0, 0);
            return;
        }
        dir.mulScalar(1 / len);

        // Camera collision: pull in if a wall is between target and desired.
        let camDist = len;
        if (this.collision) {
            const hit = this.collision.queryRay(
                target.x, target.y, target.z,
                dir.x, dir.y, dir.z, len
            );
            if (hit) {
                hitDelta.set(hit.x - target.x, hit.y - target.y, hit.z - target.z);
                const t = hitDelta.length();
                camDist = Math.max(t - this.cameraMargin, 0.3);
            }
        }

        state.cameraOffset.copy(dir).mulScalar(camDist);
    }

    private _updateAvatarCameraVisibility(state: CameraState): void {
        const cameraDistance = state.cameraOffset.length();
        if (cameraDistance <= AVATAR_CAMERA_HIDE_DISTANCE) {
            this._avatarHiddenByCamera = true;
        } else if (cameraDistance >= AVATAR_CAMERA_SHOW_DISTANCE) {
            this._avatarHiddenByCamera = false;
        }
        this.avatar?.setVisible(!this._avatarHiddenByCamera);
    }

    private _clampEntryPitch(state: CameraState): void {
        const pitch = Math.max(this._minPitch, Math.min(this._maxPitch, state.angles.x));
        this._angles.x = pitch;
        this._targetAngles.x = pitch;
        state.angles.x = pitch;
    }

    private _cameraForwardAvatarYaw(state: CameraState): number {
        return state.angles.y + 180;
    }

    private _updateAvatar(dt: number): void {
        this.avatar?.clearFlightPose();
        const vx = this._velocity.x;
        const vz = this._velocity.z;
        const speed = Math.hypot(vx, vz);

        // Face the movement direction while moving; hold facing when idle.
        if (this.avatar && speed > 0.3) {
            const yawDeg = Math.atan2(vx, vz) * 180 / Math.PI;
            this.avatar.setFacing(yawDeg, dt);
        }

        const inputs: AnimInputs = {
            speed,
            grounded: this._grounded,
            jumping: this._jumping,
            running: this._lastInputRunning || speed > this.moveGroundSpeed * 1.4
        };
        this.anim?.update(dt, inputs);
        this.avatar?.lockRootMotion(this.anim?.manualActionActive ? 'action' : 'locomotion', this.anim?.manualActionName);
    }

    private _updateFlightAvatar(dt: number, state: CameraState, forwardAmount = 0, yaw?: number, strafeAmount = 0): void {
        const avatarYaw = yaw !== undefined ? yaw : this._cameraForwardAvatarYaw(state);
        this.avatar?.setFlightPose(avatarYaw, state.angles.x, forwardAmount, dt, strafeAmount);
        if (!this.anim?.manualActionActive) this.anim?.forceIdle();
        this.avatar?.lockRootMotion(this.anim?.manualActionActive ? 'action' : 'locomotion', this.anim?.manualActionName);
    }

    /** Wheel: adjust follow distance within [min, max]. */
    adjustDistance(delta: number): void {
        this.distance = Math.max(this.minDistance, Math.min(this.maxDistance, this.distance + delta));
    }

    resetToSpawn(state: CameraState): boolean {
        const ok = super.resetToSpawn(state);
        if (ok && this.moveMode === 'fly') {
            this._flyMover.reset(this._position);
            this._flightAvatarYaw = this._cameraForwardAvatarYaw(state);
            this._flightAvatarYawTarget = this._flightAvatarYaw;
            this._updateCamera(state);
            this._updateAvatarCameraVisibility(state);
            this._updateFlightAvatar(0, state);
        } else if (ok) {
            this.avatar?.snapFacing(this._cameraForwardAvatarYaw(state));
        }
        return ok;
    }
}

export { ThirdPersonController };
